import { useEffect, useSyncExternalStore } from 'react';

// localStorage-backed cart. Subscribers re-render when the cart changes via
// either a same-tab dispatch on a custom event or the cross-tab `storage`
// event. We DON'T need a server-side cart for v1 -- the checkout API call
// takes the cart as its body and the server creates the order from it. If
// cart sharing becomes a thing, swap localStorage for a server endpoint and
// the hook signature stays the same.

export type BindingType = 'FINGERPRINT_HEX' | 'MAC_ADDRESS' | 'NODE_UUID';

export interface CartItem {
  readonly skuId: string;
  readonly displayName: string;
  readonly bindingType: BindingType;
  readonly bindingValue: string; // empty string until the user enters one
  readonly deploymentNotes: string;
}

const STORAGE_KEY = 'z9-store-cart-v1';
const CART_CHANGED_EVENT = 'z9-store-cart-changed';

interface CartState {
  readonly items: ReadonlyArray<CartItem>;
}

function read(): CartState {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw) as { items?: CartItem[] };
    return { items: Array.isArray(parsed.items) ? parsed.items : [] };
  } catch {
    return { items: [] };
  }
}

function write(state: CartState): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new Event(CART_CHANGED_EVENT));
}

// SSR-safe snapshot reference -- the React docs require returning the same
// object reference between calls when nothing changed. We cache the last
// snapshot and reuse it if `items` is shallow-equal.
let cachedSnapshot: CartState | null = null;

function getSnapshot(): CartState {
  const fresh = read();
  if (cachedSnapshot && shallowEqualItems(cachedSnapshot.items, fresh.items)) return cachedSnapshot;
  cachedSnapshot = fresh;
  return fresh;
}

function getServerSnapshot(): CartState {
  return { items: [] };
}

function shallowEqualItems(a: ReadonlyArray<CartItem>, b: ReadonlyArray<CartItem>): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (x.skuId !== y.skuId || x.bindingType !== y.bindingType || x.bindingValue !== y.bindingValue || x.deploymentNotes !== y.deploymentNotes || x.displayName !== y.displayName) {
      return false;
    }
  }
  return true;
}

function subscribe(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(CART_CHANGED_EVENT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(CART_CHANGED_EVENT, cb);
    window.removeEventListener('storage', cb);
  };
}

export interface UseCartResult {
  readonly items: ReadonlyArray<CartItem>;
  readonly count: number;
  add(item: Omit<CartItem, 'bindingValue' | 'deploymentNotes'> & { bindingValue?: string; deploymentNotes?: string }): void;
  updateBinding(skuId: string, bindingValue: string): void;
  updateNotes(skuId: string, deploymentNotes: string): void;
  remove(skuId: string): void;
  clear(): void;
}

export function useCart(): UseCartResult {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    items: state.items,
    count: state.items.length,
    add(item) {
      const items = state.items.filter((i) => i.skuId !== item.skuId);
      items.push({
        skuId: item.skuId,
        displayName: item.displayName,
        bindingType: item.bindingType,
        bindingValue: item.bindingValue ?? '',
        deploymentNotes: item.deploymentNotes ?? ''
      });
      write({ items });
    },
    updateBinding(skuId, bindingValue) {
      const items = state.items.map((i) => (i.skuId === skuId ? { ...i, bindingValue } : i));
      write({ items });
    },
    updateNotes(skuId, deploymentNotes) {
      const items = state.items.map((i) => (i.skuId === skuId ? { ...i, deploymentNotes } : i));
      write({ items });
    },
    remove(skuId) {
      write({ items: state.items.filter((i) => i.skuId !== skuId) });
    },
    clear() {
      write({ items: [] });
    }
  };
}

// Suppress unused-warning on the empty-deps effect pattern downstream.
export function useClearCart(): () => void {
  const { clear } = useCart();
  useEffect(() => undefined, []);
  return clear;
}

// Pure -- exported so unit tests can hit it directly. Returns null if every
// item has a binding; otherwise a short reason string.
export function cartReadiness(items: ReadonlyArray<CartItem>): string | null {
  if (items.length === 0) return 'cart is empty';
  for (const i of items) {
    if (!i.bindingValue.trim()) return `binding required for ${i.displayName}`;
  }
  return null;
}
